import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { useOnboardingContext } from '../contexts/OnboardingContext'
import { FlowVariant, FlowType } from '../types/onboarding.types'
import toast from 'react-hot-toast'

export const FlowTestPage = () => {
  const navigate = useNavigate()
  const { initializeFlow, progress } = useOnboardingContext()
  const [isInitializing, setIsInitializing] = useState(false)
  const [debugInfo, setDebugInfo] = useState<object | null>(null)

  const flowOptions = [
    {
      type: FlowVariant.PERSONA_MORAL,
      title: 'Persona Moral (Empresa)',
      description: 'Flujo completo para empresas - 12 pasos',
      color: 'bg-blue-500 hover:bg-blue-600',
      steps: [
        'application-start → password_change',
        'company-data → company_data', 
        'ciec → ciec_consultation',
        'credit-bureau-consultation → company_bureau_check',
        'majority-shareholder-data → personal_data',
        'personal-credit-bureau-consultation → bureau_check',
        'legal-representative → legal_representative',
        'documents → document_upload',
        'schedule-visit → ocular_visit_scheduling',
        'proposal-review → quote_review',
        'schedule-contract-signing → schedule_signing',
        'contract-signing → contract_signing'
      ]
    },
    {
      type: FlowVariant.PERSONA_FISICA,
      title: 'Persona Física (Individual)',
      description: 'Flujo completo para individuos - 9 pasos',
      color: 'bg-green-500 hover:bg-green-600',
      steps: [
        'application-start → password_change',
        'personal-data → personal_data',
        'bureau-check-individual → bureau_check',
        'ciec-individual → ciec_consultation',
        'documents-individual → document_upload',
        'schedule-visit-individual → ocular_visit_scheduling',
        'proposal-review → quote_review',
        'schedule-contract-signing → schedule_signing',
        'contract-signing → contract_signing'
      ]
    }
  ]

  const handleStartFlow = async (flowType: FlowVariant) => {
    setIsInitializing(true)
    
    try {
      console.log(`🚀 Iniciando flujo: ${flowType}`)
      
      // Initialize the onboarding flow
      await initializeFlow(FlowType.LEASING, flowType)
      
      toast.success(`Flujo ${flowType} inicializado correctamente`)
      
      // Navigate to application start with flow parameter
      navigate(`/onboarding/application-start?flow=${flowType}`)
      
    } catch (error) {
      console.error('❌ Error inicializando flujo:', error)
      toast.error('Error al inicializar el flujo. Ver consola para más detalles.')
    } finally {
      setIsInitializing(false)
    }
  }

  const handleShowDebugInfo = () => {
    console.log('📊 Debug Info - Current Progress:', progress)
    setDebugInfo(progress)
  }

  const handleResetFlow = () => {
    // Clear any existing progress (this would require implementing a reset method)
    setDebugInfo(null)
    localStorage.removeItem('onboarding-progress') // If using localStorage
    toast.success('Flujo reseteado')
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            🧪 Test de Flujos de Onboarding
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Selecciona un flujo para probar la implementación completa
          </p>
          
          {/* Debug Controls */}
          <div className="flex justify-center space-x-4 mb-8">
            <Button
              variant="secondary"
              onClick={handleShowDebugInfo}
              className="text-sm"
            >
              📊 Ver Debug Info
            </Button>
            <Button
              variant="secondary"
              onClick={handleResetFlow}
              className="text-sm"
            >
              🔄 Reset Flow
            </Button>
          </div>
        </div>

        {/* Flow Options */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {flowOptions.map((option) => (
            <div key={option.type} className="bg-white rounded-lg shadow-lg p-8 border border-gray-200">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {option.title}
                </h2>
                <p className="text-gray-600 mb-6">{option.description}</p>
                
                <Button
                  onClick={() => handleStartFlow(option.type)}
                  disabled={isInitializing}
                  className={`w-full ${option.color} text-white font-medium py-3 px-6 rounded-lg transition-colors`}
                >
                  {isInitializing ? '⏳ Inicializando...' : `🚀 Iniciar ${option.type === 'persona_moral' ? 'PM' : 'PF'}`}
                </Button>
              </div>

              {/* Steps List */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">📋 Pasos del flujo:</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {option.steps.map((step, index) => (
                    <div key={index} className="flex items-center text-sm">
                      <span className="bg-gray-100 text-gray-700 rounded-full w-6 h-6 flex items-center justify-center mr-3 text-xs font-medium">
                        {index + 1}
                      </span>
                      <code className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                        {step}
                      </code>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Debug Info Display */}
        {debugInfo && (
          <div className="bg-gray-900 text-green-400 p-6 rounded-lg mb-8">
            <h3 className="text-lg font-bold mb-4">🐛 Debug Information:</h3>
            <pre className="text-xs overflow-auto">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        )}

        {/* Current Status */}
        {progress && (
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">📈 Estado Actual:</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <strong>Flujo:</strong> {progress.flow?.variant || 'No iniciado'}
              </div>
              <div>
                <strong>Estado:</strong> {progress.status || 'N/A'}
              </div>
              <div>
                <strong>Progreso:</strong> {progress.completionPercentage || 0}%
              </div>
              {progress.currentStepKey && (
                <div className="md:col-span-3">
                  <strong>Paso actual:</strong> <code>{progress.currentStepKey}</code>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-yellow-900 mb-4">📝 Instrucciones de Testing:</h3>
          <ul className="list-disc list-inside space-y-2 text-yellow-800 text-sm">
            <li>Selecciona un flujo para inicializar el onboarding</li>
            <li>Sigue cada paso en orden y verifica la navegación</li>
            <li>Usa "Ver Debug Info" para inspeccionar el estado interno</li>
            <li>Verifica que cada paso redirectione correctamente al siguiente</li>
            <li>Prueba los botones "Cancelar" para verificar navegación hacia atrás</li>
            <li>Usa "Reset Flow" para limpiar el estado y probar desde cero</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default FlowTestPage